import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MarketResponse, MarketRequest, MarketsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-market',
    templateUrl: './edit-market.component.html',
    styleUrl: './edit-market.component.scss',
    host: {
        class: 'app-edit-market'
    },
    standalone: false
})
export class EditMarketComponent extends BaseViewComponent implements OnInit {

  marketId: number = 0;
  market: MarketResponse = {};

  filterOperator = FilterOperator;
  marketsList: any[] = [];
  marketsTotalRecords: number = 0;
  marketsRowsPerPageOption: number = 10;

  public marketForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    warrantyDurationMonths: ['', [Validators.required]],
    hasParentMarket: ['false'],
    market: [[]]
  },
    { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private marketsService: MarketsService,
    private fb: FormBuilder
  ) {
    super();
   }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.marketId = params['marketId'];

      if (this.marketId) {
        await this.getMarketId();
      }
    });
  }

  async getMarketId() {
    this.market = await firstValueFrom(this.marketsService.apiMarketsIdGet(this.marketId));
    this.initForm();
    await this.getMarkets({ pageIndex: 1, search: '' });
  }

  onLazyLoadMarkets(event: any) {
    this.getMarkets(event);
  }

  async getMarkets(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const pageSize = Number(event?.pageSize) || this.marketsRowsPerPageOption;

    this.marketsRowsPerPageOption = pageSize;
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: this.marketsRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    const marketsPage = await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(payload));

    // prevent adding itself as parent
    this.marketsList = (marketsPage.results ?? []).filter(c => c.id != this.marketId);
    this.marketsTotalRecords = marketsPage.totalCount ?? 0;

    if (this.market?.parent?.id && this.market.parent.id != this.marketId) {
      const exists = this.marketsList.some((c: any) => c.id == this.market.parent?.id);
      if (!exists) {
        const current = await firstValueFrom(this.marketsService.apiMarketsComboValueIdGet(this.market.parent?.id))
        this.marketsList = [current, ...this.marketsList];
      }
    }

  }

  getMarketsTotalPages(): number {
    const total = Number(this.marketsTotalRecords) || 0;
    const pageSize = Number(this.marketsRowsPerPageOption) || 10;

    return Math.max(1, Math.ceil(total / pageSize));
  }

  initForm() {
    this.marketForm.controls['name'].setValue(this.market.name);
    this.marketForm.controls['code'].setValue(this.market.code);
    this.marketForm.controls['warrantyDurationMonths'].setValue(this.market.warrantyDurationMonths);

    if (this.market.parentId) {
      this.marketForm.controls['hasParentMarket'].setValue('true');
      this.marketForm.controls['market'].setValue([this.market.parentId]);
    } else {
      this.marketForm.controls['hasParentMarket'].setValue('false');
      this.marketForm.controls['market'].setValue([]);
    }

    this.marketForm.get('hasParentMarket')?.valueChanges.subscribe((hasParent) => {
      if (hasParent === 'false') {
        this.marketForm.controls['market'].setValue([]);
      }
    });
  }

  isMarketRequired(): boolean {
    return this.marketForm.get('hasParentMarket')?.value === 'true';
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentMarket = formGroup.get('hasParentMarket')?.value;
      const market = formGroup.get('market')?.value;

      const hasSelectedMarket = Array.isArray(market)
        ? market.length > 0
        : !!market;

      if (hasParentMarket === 'true' && !hasSelectedMarket) {
        return { requiredMarket: true };
      }

      return null;
    };
  }

  async submitForm() {
    if (this.marketForm.valid) {

      const payload: MarketRequest = {
        name: this.marketForm.controls['name'].value,
        code: this.marketForm.controls['code'].value,
        parentId: this.marketForm.controls['market'].value?.[0],
        warrantyDurationMonths: this.marketForm.controls['warrantyDurationMonths'].value
      };

      await firstValueFrom(this.marketsService.apiMarketsUpdateIdPut(this.marketId, payload));

      this.router.navigate(['/markets', this.marketId]);
    }
  }
}
